import 'react-datepicker/dist/react-datepicker.css';

import * as Styled from './PunchupDateTimePicker.styles';

import DatePicker from 'react-datepicker';
import { FC } from 'react';
import { FlexContainer } from '../../FlexContainer/FlexContainer';
import { get, useFormContext } from 'react-hook-form';
import {
	PunchupFieldHelp,
	PunchupFieldLabel,
} from 'features/common/components/Punchup/primitives/forms/fields/PunchupFieldLabel';

interface PunchupDateTimePickerProps {
	id: string;
	name: string;
	label?: string;
	required?: boolean;
	/** Optional "explain this field" tooltip beside the label. */
	help?: PunchupFieldHelp;
	onChange?: (date: Date) => void;
	minDate?: Date;
	minTime?: Date;
	maxDate?: Date;
	maxTime?: Date;
}

export const PunchupDateTimePicker: FC<PunchupDateTimePickerProps> = ({
	id,
	name,
	label,
	required,
	help,
	onChange,
	minDate,
	minTime,
	maxDate,
	maxTime,
}) => {
	const {
		formState: { errors },
		watch,
		setValue,
	} = useFormContext();

	const value = watch(name);

	// get() resolves nested paths like "dates[0].end_date" that a flat key lookup cannot — the
	// cross-field errors (end < start, doors after start) live at the nested path.
	const error = get(errors, name)?.message;

	const handleChange = (date: Date) => {
		// shouldValidate on every pick: a date that breaks a cross-field rule turns red the moment it is chosen,
		// and a corrected one clears live. Save re-runs the same check. shouldDirty marks the field changed.
		setValue(name, date, { shouldValidate: true, shouldDirty: true });
		onChange?.(date);
	};

	const parsedValue = value ? new Date(value) : null;

	return (
		<FlexContainer flexDirection="column" boxSizing="border-box" marginBottom="12px" flex="1">
			{label && (
				// No `required`: the classic form marks optional fields instead of asterisking required ones.
				<PunchupFieldLabel
					label={
						<>
							{label}
							{required === false && <Styled.OptionalSpan>Optional</Styled.OptionalSpan>}
						</>
					}
					htmlFor={id}
					help={help}
					helpAriaLabel={`${label} — more information`}
				/>
			)}
			<Styled.InputContainer hasError={Boolean(error)}>
				<DatePicker
					id={id}
					name={name}
					selected={parsedValue}
					onChange={handleChange}
					showTimeSelect
					timeIntervals={15}
					dateFormat="MM/dd/yyyy h:mm aa"
					placeholderText="mm/dd/yyyy h:mm"
					timeCaption="Time"
					autoComplete="off"
					className="punchup-datepicker"
					minDate={minDate}
					minTime={minTime}
					maxDate={maxDate}
					maxTime={maxTime}
				/>
				<Styled.CalendarIcon iconName="calendar" />
			</Styled.InputContainer>
			{/* The shared error class the whole form uses, so an invalid Save can find and scroll to it. */}
			{error && <span className="punchupFormError">{error}</span>}
		</FlexContainer>
	);
};
